import { useEffect, useRef, useState } from "react";
import {
  Alert, Anchor, Badge, Button, Card, Group, Loader, Stack, Text, ThemeIcon, Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconCheck, IconDownload, IconExternalLink, IconShieldSearch, IconX,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { downloadFile } from "../../../Boilerplate/api";
import {
  confirmBackgroundCheckIdentity, createBackgroundCheck, getBackgroundCheck,
  listBackgroundChecks, rejectBackgroundCheckIdentity,
} from "../api";
import { useEnrichmentCostBasis } from "../hooks/useEnrichmentCostBasis";
import { creditPrice } from "../_Shared/enrichment";
import { formatDateTime } from "../_Shared/time";

const OPEN_STATUSES = new Set(["awaitingIdentityConfirmation", "running"]);

function severityColor(s) {
  if (s === "critical") return "red";
  if (s === "noteworthy") return "orange";
  return "gray";
}

function verdictColor(v) {
  if (v === "consistent") return "green";
  if (v === "inconsistent") return "red";
  return "gray";
}

function isOpen(check) {
  return OPEN_STATUSES.has(check?.status);
}

function needsPoll(check) {
  if (!check) return false;
  if (check.status === "running") return true;
  if (check.status === "awaitingIdentityConfirmation")
    return !check.identitySnapshot || !!check.identityConfirmedAt;
  return false;
}

export default function BackgroundCheckPanel({ candidateId }) {
  const { t, i18n } = useTranslation("Veft");
  const costBasis = useEnrichmentCostBasis();
  const credits = costBasis?.backgroundCheckCredits ?? 2000;
  const price = creditPrice(costBasis, credits, i18n.language);

  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  async function loadList() {
    const items = await listBackgroundChecks(candidateId);
    const next = items ?? [];
    setList(next);
    return next;
  }

  async function loadOpenOrKeep(items) {
    const open = (items ?? []).find(isOpen);
    if (open) {
      const detail = await getBackgroundCheck(open.id);
      setActive(detail);
      return detail;
    }
    setActive((prev) => (prev && isOpen(prev) ? null : prev));
    return null;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listBackgroundChecks(candidateId);
        if (cancelled) return;
        setList(items ?? []);
        const open = (items ?? []).find(isOpen);
        if (open) {
          const detail = await getBackgroundCheck(open.id);
          if (!cancelled) setActive(detail);
        }
      } catch {
        if (!cancelled) notifications.show({ color: "red", title: t("error"), message: t("bg_load_failed") });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [candidateId, t]);

  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!needsPoll(active)) return undefined;
    pollRef.current = setInterval(async () => {
      try {
        const detail = await getBackgroundCheck(active.id);
        setActive(detail);
        if (!isOpen(detail)) loadList().catch(() => {});
      } catch { /* keep last frame */ }
    }, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [active?.id, active?.status, active?.identitySnapshot, active?.identityConfirmedAt]);

  async function startCheck() {
    setBusy(true);
    try {
      const created = await createBackgroundCheck(candidateId, i18n.language?.startsWith("de") ? "de" : "en");
      const detail = await getBackgroundCheck(created.id);
      setActive(detail);
      await loadList();
    } catch (err) {
      if (err?.status === 402) return;
      notifications.show({ color: "red", title: t("error"), message: err?.message || t("bg_start_failed") });
      try { await loadOpenOrKeep(await loadList()); } catch { /* ignore */ }
    } finally {
      setBusy(false);
    }
  }

  async function confirmIdentity() {
    if (!active) return;
    setBusy(true);
    try {
      await confirmBackgroundCheckIdentity(active.id);
      const detail = await getBackgroundCheck(active.id);
      setActive(detail);
    } catch (err) {
      if (err?.status === 402) return;
      notifications.show({ color: "red", title: t("error"), message: err?.message || t("bg_confirm_failed") });
    } finally {
      setBusy(false);
    }
  }

  async function rejectIdentity() {
    if (!active) return;
    setBusy(true);
    try {
      await rejectBackgroundCheckIdentity(active.id);
      setActive(null);
      await loadList();
    } catch (err) {
      notifications.show({ color: "red", title: t("error"), message: err?.message || t("bg_reject_failed") });
    } finally {
      setBusy(false);
    }
  }

  async function openCheck(id) {
    try {
      setActive(await getBackgroundCheck(id));
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("bg_load_failed") });
    }
  }

  if (loading) return <Loader />;

  const snapshot = active?.identitySnapshot;
  const awaitingUser = active?.status === "awaitingIdentityConfirmation" && snapshot && !active.identityConfirmedAt;
  const resolving = active?.status === "awaitingIdentityConfirmation" && !snapshot && !active.errorMessage;
  const running = active?.status === "running" || (active?.status === "awaitingIdentityConfirmation" && active.identityConfirmedAt);
  const history = list.filter((c) => !isOpen(c));

  return (
    <Stack gap="lg">
      <div>
        <Group gap="xs" mb={4}>
          <ThemeIcon variant="light" color="blue"><IconShieldSearch size={18} /></ThemeIcon>
          <Title order={5}>{t("bg_title")}</Title>
        </Group>
        <Text size="sm" c="dimmed">{t("bg_intro")}</Text>
      </div>

      {!isOpen(active) && (
        <Card withBorder>
          <Stack gap="sm">
            <Text size="sm">{t("bg_when")}</Text>
            <Group>
              <Button
                leftSection={<IconShieldSearch size={16} />}
                onClick={startCheck}
                loading={busy}
              >
                {t("bg_start")}
              </Button>
              {price && (
                <Text size="xs" c="dimmed">{t("bg_cost", { credits, price })}</Text>
              )}
            </Group>
            <Text size="xs" c="dimmed">{t("bg_charged_on_confirm")}</Text>
          </Stack>
        </Card>
      )}

      {resolving && (
        <Card withBorder>
          <Group>
            <Loader size="sm" />
            <Text size="sm">{t("bg_resolving")}</Text>
          </Group>
        </Card>
      )}

      {awaitingUser && (
        <Card withBorder>
          <Stack gap="md">
            <Alert color="blue" variant="light">{t("bg_gate_help")}</Alert>
            <div>
              <Text fw={600} size="lg">{snapshot.displayName}</Text>
              {snapshot.title && <Text size="sm">{snapshot.title}</Text>}
              {snapshot.currentCompany && (
                <Text size="sm" c="dimmed">
                  {snapshot.currentCompany}
                  {snapshot.tenureSummary ? ` · ${snapshot.tenureSummary}` : ""}
                </Text>
              )}
              {snapshot.location && <Text size="sm" c="dimmed">{snapshot.location}</Text>}
              {snapshot.linkedInUrl && (
                <Anchor href={snapshot.linkedInUrl} target="_blank" rel="noopener noreferrer" size="sm">
                  LinkedIn <IconExternalLink size={12} />
                </Anchor>
              )}
            </div>
            <Text size="sm">{t("bg_gate_question")}</Text>
            {price && <Text size="xs" c="dimmed">{t("bg_cost", { credits, price })}</Text>}
            <Group>
              <Button leftSection={<IconCheck size={16} />} onClick={confirmIdentity} loading={busy}>
                {t("bg_confirm")}
              </Button>
              <Button variant="default" leftSection={<IconX size={16} />} onClick={rejectIdentity} disabled={busy}>
                {t("bg_reject")}
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {running && (
        <Card withBorder>
          <Stack gap="sm">
            <Group>
              <Loader size="sm" />
              <Text size="sm">{t("bg_running")}</Text>
            </Group>
            <Text size="xs" c="dimmed">{t("bg_running_help")}</Text>
            {active.findings?.length > 0 && <FindingsList findings={active.findings} t={t} />}
          </Stack>
        </Card>
      )}

      {active?.status === "failed" && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title={t("bg_failed")}>
          {mapError(active.errorMessage, t)}
          <Group mt="sm">
            <Button size="xs" onClick={() => { setActive(null); startCheck(); }} loading={busy}>
              {t("bg_retry")}
            </Button>
          </Group>
        </Alert>
      )}

      {active?.status === "rejected" && (
        <Alert color="gray" title={t("bg_rejected_title")}>
          {t("bg_rejected_body")}
          <Group mt="sm">
            <Button size="xs" variant="light" onClick={() => setActive(null)}>{t("bg_start")}</Button>
          </Group>
        </Alert>
      )}

      {active?.status === "complete" && (
        <Report check={active} t={t} i18n={i18n} />
      )}

      {history.length > 0 && (
        <Stack gap="xs">
          <Text size="sm" fw={500}>{t("bg_history")}</Text>
          {history.map((c) => (
            <Card
              key={c.id}
              p="sm"
              withBorder
              style={{ cursor: "pointer" }}
              onClick={() => openCheck(c.id)}
            >
              <Group justify="space-between">
                <Group gap="xs">
                  <Badge size="xs" variant="light" color={statusColor(c.status)}>
                    {t(`bg_status_${c.status}`)}
                  </Badge>
                  <Text size="sm">{formatDateTime(c.createdAt, i18n.language)}</Text>
                </Group>
                {c.status === "complete" && (
                  <Text size="xs" c="dimmed">{t("bg_finding_count", { count: c.findingCount })}</Text>
                )}
              </Group>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function Report({ check, t, i18n }) {
  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">{formatDateTime(check.createdAt, i18n.language)}</Text>
        <Button
          size="xs"
          variant="light"
          leftSection={<IconDownload size={14} />}
          onClick={() => downloadFile(`background-checks/${check.id}/pdf`)}
        >
          {t("bg_download_pdf")}
        </Button>
      </Group>
      <Alert color="gray" variant="light">{t("bg_report_disclaimer")}</Alert>
      <Title order={6}>{t("bg_findings")}</Title>
      {check.findings?.length ? (
        <FindingsList findings={check.findings} t={t} />
      ) : (
        <Text size="sm" c="dimmed">{t("bg_no_findings")}</Text>
      )}
      <Title order={6}>{t("bg_claims")}</Title>
      {check.claims?.length ? (
        <Stack gap="xs">
          {check.claims.map((c, i) => (
            <Card key={i} p="sm" withBorder>
              <Group gap="xs" mb={4}>
                <Badge size="xs" color={verdictColor(c.verdict)} variant="light">
                  {t(`bg_verdict_${c.verdict}`)}
                </Badge>
                <Badge size="xs" variant="outline">{t(`bg_claim_${c.kind}`)}</Badge>
              </Group>
              <Text size="sm">{c.expected}</Text>
              {c.evidenceQuote && (
                <Text size="sm" fs="italic" mt={4}>„{c.evidenceQuote}“</Text>
              )}
              {c.evidenceUrl && (
                <Anchor href={c.evidenceUrl} target="_blank" rel="noopener noreferrer" size="xs">
                  {c.evidenceUrl}
                </Anchor>
              )}
            </Card>
          ))}
        </Stack>
      ) : (
        <Text size="sm" c="dimmed">{t("bg_no_claims")}</Text>
      )}
    </Stack>
  );
}

function FindingsList({ findings, t }) {
  return (
    <Stack gap="xs">
      {findings.map((f, i) => (
        <Card key={i} p="sm" withBorder>
          <Group gap="xs" mb={4}>
            <Badge size="xs" color={severityColor(f.severity)} variant="filled">
              {t(`bg_severity_${f.severity}`)}
            </Badge>
            <Badge size="xs" variant="outline">{t(`bg_cat_${f.category}`)}</Badge>
          </Group>
          <Text size="sm" fw={500}>{f.title}</Text>
          <Text size="sm" fs="italic" mt={4}>„{f.verbatimQuote}“</Text>
          <Anchor href={f.url} target="_blank" rel="noopener noreferrer" size="xs">
            {f.url}
          </Anchor>
        </Card>
      ))}
    </Stack>
  );
}

function statusColor(status) {
  if (status === "complete") return "green";
  if (status === "failed") return "red";
  if (status === "rejected") return "gray";
  return "blue";
}

function mapError(message, t) {
  if (!message) return t("bg_failed_generic");
  if (/kontext|identity/i.test(message)) return t("bg_insufficient_identity");
  if (/credit/i.test(message)) return t("bg_insufficient_credits");
  return message;
}
